import { createContext, useContext, useEffect } from "react";
import type { PropsWithChildren } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "../hooks/useToast";
import { IDENTITY_KEY, clearOrdinalStorage, identityFor, leaveEndedViewAs, syncIdentity, type ViewAs } from "../lib/viewAsIdentity";
import { installViewOnlyFetchGuard } from "../lib/viewOnlyRefusals";

export interface User {
  id: number;
  email: string;
  /** Legacy DB enum; omitted from /api/v1/logged_in — use platform_* booleans for auth. */
  role?: string;
  stripe_customer_id: string | null;
  created_at: string;
  updated_at: string;
  // Indicates if the user has access to the Grants feature
  grant_access: boolean;
  grant_suite?: boolean;
  /** Discover tier: grant_access or grant_suite (from session). */
  grant_discover?: boolean;
  /** Outside collaborator (ENG-1820): linked to an external org, on no team. View-only. */
  outside_user?: boolean;
  /** Grant-only app shell for outside users (feature-flagged; UI only). */
  outside_user_shell?: boolean;
  /** Grant Team Manager on at least one team (ENG-1821). Offers the area only. */
  grant_team_manager?: boolean;
  grant_team_manager_team_ids?: number[];
  // Indicates if the user has access to the Grant Finder doc demo (PDF/application generation)
  grant_finder_doc_demo?: boolean;
  // Indicates if the user has access to the Code Doctor feature
  code_doctor: boolean;
  // Indicates if the user has access to the Connect Analytics feature
  connect_analytics?: boolean;
  // Indicates if the user has access to the Connect Analytics Live feature
  connect_analytics_live?: boolean;
  // Indicates if the user has access to Staff Reports
  staff_reports_access?: boolean;
  // Indicates if the user has access to Code Library
  code_library_access?: boolean;
  // Indicates if the user has access to Legislation Monitor
  legislation_monitor_access?: boolean;
  legislation_monitor_state_abbreviations?: string[];
  // Indicates if the user has access to Case Portal
  case_portal_access?: boolean;
  // Indicates if the user has access to Permit Assistant (backed by the form extraction flow)
  form_extraction_ui?: boolean;
  // Indicates if the user has access to Plan Review
  plan_review_access?: boolean;
  site_plan_intake?: boolean;
  permit_suite?: boolean;
  // Municipalities where Development Suite is open to this user (team Flipper, per workspace)
  development_suite_municipality_ids?: number[];
  trust_suite?: boolean;
  // Municipality Resource Links self-service manager (team Flipper)
  municipality_resource_links_access?: boolean;
  // Browse-only municipal PDF library (team Flipper)
  file_viewer?: boolean;
  // Indicates if the user is on the RA latency optimization v1 path
  research_assistant_latency_optimization_v1?: boolean;
  // Indicates if the user has access to the LLM sandbox
  sandbox_access?: boolean;
  can_manage_team_users?: boolean;
  manageable_team_ids?: number[];
  platform_employee?: boolean;
  platform_support?: boolean;
  platform_admin?: boolean;
  can_access_sandbox_audit?: boolean;
  can_access_support_console?: boolean;
}

type UserContextType = {
  currentUser: User | null;
  updateCurrentUser: (user: User | null) => void;
  logout: () => void;
  validateUser: () => void;
};

const UserContext = createContext<UserContextType | null>(null);

export const useUser = () => {
  const value = useContext(UserContext);

  if (value === null) {
    throw new Error("useUser cannot be called without a UserProvider");
  }

  return value;
};

// For display-only consumers that may render outside a UserProvider (and in
// component tests that don't mount one): null instead of throwing. Anything
// gating real behavior should keep useUser's fail-fast contract.
export const useCurrentUser = (): User | null => {
  return useContext(UserContext)?.currentUser ?? null;
};

const UserProvider = ({ children }: PropsWithChildren) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { data } = useQuery<{ user: User | null; view_as?: ViewAs | null }>({
    queryKey: ["currentUser"],
    queryFn: () =>
      fetch("/api/v1/logged_in", {
        method: "GET",
        headers: {
          Accept: "application/json",
        },
        credentials: "include",
      }).then(async (response) => {
        // A view-as session ended (timeout, exit elsewhere, lost access). Leave
        // the customer's view rather than carry on as a different identity.
        if (response.status === 409) {
          const body = await response.json().catch(() => ({}));
          leaveEndedViewAs(body.location);
          throw Error("view_as_ended");
        }
        if (!response.ok) {
          throw Error(response.statusText);
        }
        return response.json();
      }),
    retry: false,
    // Cheap, and the moment a stale tab is used again is when it must notice
    // that the identity behind it changed.
    refetchOnWindowFocus: true,
  });

  const userId = data?.user?.id;
  const viewAs = data?.view_as ?? null;
  const identity = userId === undefined ? null : identityFor(userId, viewAs);

  useEffect(() => {
    if (identity === null) return;
    if (syncIdentity(identity, viewAs !== null)) {
      window.location.reload();
      return;
    }

    const onStorage = (event: StorageEvent) => {
      if (event.key === IDENTITY_KEY && event.newValue !== identity) {
        window.location.reload();
      }
    };
    window.addEventListener("storage", onStorage);

    // Ask the server once the session is due to end, so the tab leaves the
    // customer's view on time even if nobody touches it.
    const msLeft = viewAs ? Date.parse(viewAs.expires_at) - Date.now() + 1000 : null;
    const timer =
      msLeft === null ? undefined : setTimeout(() => queryClient.invalidateQueries({ queryKey: ["currentUser"] }), Math.max(msLeft, 0));

    return () => {
      window.removeEventListener("storage", onStorage);
      clearTimeout(timer);
    };
  }, [identity, viewAs, queryClient]);

  const viewing = viewAs !== null;
  useEffect(() => {
    if (!viewing) return;
    return installViewOnlyFetchGuard((message) => toast({ title: "View only", description: message }));
  }, [viewing, toast]);

  const logoutMutation = useMutation({
    mutationFn: () => {
      const csrfToken = document.querySelector("meta[name='csrf-token']")?.getAttribute("content") ?? "";

      clearOrdinalStorage();

      return fetch("/users/sign_out", {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": csrfToken,
        },
        credentials: "include",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["currentUser"] });
      window.location.href = "/";
    },
    onError: () => {
      // TODO: Add observability
      toast({ title: "There was a problem logging out. Please try again.", variant: "destructive" });
    },
  });

  const contextValue = {
    currentUser: data?.user ?? null,
    updateCurrentUser: (user: User | null) => queryClient.setQueryData(["currentUser"], { user }),
    logout: () => logoutMutation.mutate(),
    validateUser: () => queryClient.invalidateQueries({ queryKey: ["currentUser"] }),
  };

  return <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>;
};

export default UserProvider;
